/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { usePreviewWidget } from '@/components/preview/PreviewWidgetFrame';
//import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { fetcher, getESpotDataFromName } from '@/data/Content/_ESpotDataFromName-Server';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useSettings } from '@/data/Settings';
//import { usePageDataFromId } from '@/data/_PageDataFromId';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME } from '@/data/constants/dataKey';
import { EMSTYPE_LOCAL, MARKETING_SPOT_DATA_TYPE } from '@/data/constants/marketing';
import { EventsContext } from '@/data/context/events';
import { ID } from '@/data/types/Basic';
import { ESpotActivityContainer } from '@/data/types/Marketing';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getDMSubstitutions } from '@/data/utils/getDMSubstitutions';
import { getESpotBaseData } from '@/data/utils/getESpotBaseData';
//import { getESpotParams } from '@/data/utils/getESpotQueryParams';
import { expand, shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { marketingSpotMiddleWare } from '@/data/utils/swr/marketingContentMiddleWare';
import { seoObfuscatorMiddleWare } from '@/data/utils/swr/seoObfuscatorMiddleWare';
import { useContext, useEffect, useMemo } from 'react';
import useSWR from 'swr';
//import { QUERY_FIELDS_TO_DELETE_FROM_SWR_KEY } from '../constants/espotData';

export type { ESpotContainerType } from '@/data/types/ESpot';
export { getESpotDataFromName };

export const useESpotDataFromName = (emsName: ID, trackEvents = true) => {
	const { onPromotionView } = useContext(EventsContext);
	const router = useNextRouter();
	const { settings } = useSettings();
	//const { breadcrumb } = useBreadcrumbTrail();
	const { storeId, langId, defaultCatalogId: catalogId } = getClientSideCommon(settings, router);
	const { setESpotData } = usePreviewWidget();
	//const { query } = router;
	const params = useExtraRequestParameters();
	//const { data: pageData } = usePageDataFromId();
	const DM_Substitution = useMemo(
		() => getDMSubstitutions(emsName, { undefined, settings, langId }),
		[emsName, langId, settings]
	);
	const dm_substitutions = (DM_Substitution as Array<any>).reduce(
		(a, c, i) => ({
			...a,
			[`DM_SubstitutionName${i + 1}`]: c.name,
			[`DM_SubstitutionValue${i + 1}`]: c.value,
		}),
		{}
	);
	/*const queryBase = useMemo( // Commented to remove unncessory params on each espot call
		() => ({
			catalogId,
			DM_ReturnCatalogGroupId: true,
			DM_FilterResults: false,
			langId,
			DM_Substitution,
		}),
		[catalogId, langId, DM_Substitution]
	);*/

	//const espotParams = {...getESpotParams({ pageData, query, emsName, queryBase, breadcrumb })}
	//const espotParamsQ = Object.assign({}, espotParams)
	//QUERY_FIELDS_TO_DELETE_FROM_SWR_KEY.forEach(q=>delete espotParamsQ?.query[q]);

	const { data, error, isLoading } = useSWR(
		storeId
			? [
					shrink({
						emsName,
						query:{
							langId,
							catalogId,
							...dm_substitutions
						},
						storeId,
						//...espotParamsQ,
					}),
					DATA_KEY_E_SPOT_DATA_FROM_NAME,
			  ]
			: null,
		async ([props]) => {
			const expanded = expand<Record<string, any>>(props);
		return fetcher(true)(expanded.storeId, expanded.emsName, expanded.query, params);
		},
		{ use: [seoObfuscatorMiddleWare, laggyMiddleWare, marketingSpotMiddleWare] }
	);

	useEffect(() => {
		setESpotData && setESpotData(data?.MarketingSpotData);
	}, [data, setESpotData]);
	useEffect(() => {
		if (trackEvents  ) {
			const all = getESpotBaseData(data);
			all?.forEach((spot) => {
				if (MARKETING_SPOT_DATA_TYPE.CONTENT === spot?.baseMarketingSpotDataType) {
					const activity = spot as Required<ESpotActivityContainer>;
					setTimeout(()=>{
						onPromotionView({ gtm: { activity, settings } });
					}, 400);		
				}
			});
		}
		
	}, [data,onPromotionView]); // eslint-disable-line react-hooks/exhaustive-deps

	return {
		data,
		loading: !data && isLoading,
		error,
	};
};

/**
 * @deprecated to be removed
 */
export const getEMarketingSpotName = (properties: any, tokenExternalValue?: ID) => {
	if (properties?.emsName) {
		return properties?.emsType === EMSTYPE_LOCAL && tokenExternalValue
			? tokenExternalValue + properties?.emsName
			: properties?.emsName;
	} else {
		return tokenExternalValue;
	}
};
